import React from 'react';
import { createPortal } from 'react-dom';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, Button, FormControl, InputLabel, Menu, MenuItem, Select, Stack } from '@mui/material';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ActionButtons } from '../../../components/lottery/ActionButtons';
import { CreatePackSection } from '../../../components/lottery/CreatePackSection';
import CaseSimulatorDisplayCaseGrid from './CaseSimulatorDisplayCaseGrid';
import CaseSimulatorTicketsGrid from './CaseSimulatorTicketsGrid';
import type { CaseSimulatorBarcodeMode, CaseSimulatorMainSectionProps } from '../../../types/features/caseSimulator';
import imageService from '../../../services/imageService';
import { loadCaseSimulatorExportImages } from '../utils/caseSimulatorExportImages';
import CaseSimulatorBarcodeModeSelector from './CaseSimulatorBarcodeModeSelector';

const PDF_MARGIN_IN = 0.5;
const TABLOID_RASTER_SCALE = 3;
const TABLOID_JPEG_QUALITY = 0.9;
const EXPORT_IMAGE_WAIT_TIMEOUT_MS = 8000;
const EXPORT_SHEET_RENDER_TIMEOUT_MS = 2000;
const TEST_SHEET_QR_VIEWBOX_SIZE = 85;

type QrModule = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type TestSheetLayout = 'current' | 'tabloid';

const TEST_SHEET_LAYOUTS: Record<TestSheetLayout, {
  label: string;
  pageWidthIn: number;
  pageHeightIn: number;
}> = {
  current: {
    label: '19 × 28.5 in (4 columns)',
    pageWidthIn: 19,
    pageHeightIn: 28.5,
  },
  tabloid: {
    label: '11 × 17 in (3 × 5 cards)',
    pageWidthIn: 11,
    pageHeightIn: 17,
  },
};

const waitForExportImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(images.map(async (image) => {
    if (image.complete && image.naturalWidth > 0) {
      return;
    }

    if (image.complete) {
      return;
    }

    if (typeof image.decode === 'function') {
      try {
        await image.decode();
        return;
      } catch {
        // Fall through to load/error listeners for browsers that reject decode before load.
      }
    }

    await new Promise<void>((resolve) => {
      const timeoutId = window.setTimeout(resolve, EXPORT_IMAGE_WAIT_TIMEOUT_MS);
      const done = () => {
        window.clearTimeout(timeoutId);
        resolve();
      };
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    });
  }));
};

const waitForExportSheet = async (
  getExportNode: () => HTMLDivElement | null,
): Promise<HTMLDivElement> => {
  const deadline = performance.now() + EXPORT_SHEET_RENDER_TIMEOUT_MS;

  while (performance.now() < deadline) {
    const exportNode = getExportNode();
    if (exportNode) {
      return exportNode;
    }

    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  }

  throw new Error('Display case export sheet did not render');
};

const convertBlobUrlToDataUrl = async (imageUrl: string): Promise<string> => {
  if (!imageUrl.startsWith('blob:')) {
    return imageUrl;
  }

  const response = await fetch(imageUrl);
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result)), { once: true });
    reader.addEventListener('error', () => reject(reader.error), { once: true });
    reader.readAsDataURL(blob);
  });
};

const makeCanvasSafeImageUrls = async (imageUrls: Map<string, string>) => new Map(
  await Promise.all(Array.from(imageUrls.entries()).map(async ([packId, imageUrl]) => (
    [packId, await convertBlobUrlToDataUrl(imageUrl)] as const
  ))),
);

const loadTestSheetQrModules = async (): Promise<QrModule[]> => {
  const response = await fetch('/lottery-test-sheet-vcard-qr.svg');
  if (!response.ok) {
    throw new Error('Failed to load the test sheet QR code');
  }

  const svgText = await response.text();
  const pathData = new DOMParser()
    .parseFromString(svgText, 'image/svg+xml')
    .querySelector('#qr-path')
    ?.getAttribute('d');
  if (!pathData) {
    throw new Error('The test sheet QR code has no module path');
  }

  const modules: QrModule[] = [];
  const modulePattern = /M(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)H(\d+(?:\.\d+)?)V(\d+(?:\.\d+)?)H(\d+(?:\.\d+)?)z/g;
  let match = modulePattern.exec(pathData);
  while (match) {
    const [, x, y, right, bottom] = match;
    modules.push({
      x: Number(x),
      y: Number(y),
      width: Number(right) - Number(x),
      height: Number(bottom) - Number(y),
    });
    match = modulePattern.exec(pathData);
  }

  if (modules.length === 0) {
    throw new Error('The test sheet QR code modules could not be parsed');
  }
  return modules;
};

const addVectorBarcodesToPdf = (
  pdf: jsPDF,
  pageNode: HTMLElement,
  contentWidthIn: number,
  contentHeightIn: number,
) => {
  const pageRect = pageNode.getBoundingClientRect();
  if (pageRect.width <= 0 || pageRect.height <= 0) {
    return;
  }

  const toPdfX = (value: number) => (
    PDF_MARGIN_IN + ((value - pageRect.left) / pageRect.width) * contentWidthIn
  );
  const toPdfY = (value: number) => (
    PDF_MARGIN_IN + ((value - pageRect.top) / pageRect.height) * contentHeightIn
  );

  pageNode.querySelectorAll<SVGSVGElement>('.case-simulator-display-case-barcode svg')
    .forEach((barcodeSvg) => {
      const barcodeContainer = barcodeSvg.closest<HTMLElement>('.case-simulator-display-case-barcode');
      if (!barcodeContainer) {
        return;
      }

      const barcodeBounds = barcodeSvg.getBoundingClientRect();
      const containerBounds = barcodeContainer.getBoundingClientRect();
      const visibleLeft = Math.max(barcodeBounds.left, containerBounds.left);
      const visibleTop = Math.max(barcodeBounds.top, containerBounds.top);
      const visibleRight = Math.min(barcodeBounds.right, containerBounds.right);
      const visibleBottom = Math.min(barcodeBounds.bottom, containerBounds.bottom);
      if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
        return;
      }

      pdf.setFillColor(255, 255, 255);
      pdf.rect(
        toPdfX(visibleLeft),
        toPdfY(visibleTop),
        ((visibleRight - visibleLeft) / pageRect.width) * contentWidthIn,
        ((visibleBottom - visibleTop) / pageRect.height) * contentHeightIn,
        'F',
      );

      barcodeSvg.querySelectorAll<SVGRectElement>('rect').forEach((bar) => {
        const fill = window.getComputedStyle(bar).fill.replace(/\s/g, '').toLowerCase();
        if (fill !== 'rgb(0,0,0)' && fill !== '#000000' && fill !== 'black') {
          return;
        }

        const bounds = bar.getBoundingClientRect();
        const left = Math.max(bounds.left, visibleLeft);
        const top = Math.max(bounds.top, visibleTop);
        const right = Math.min(bounds.right, visibleRight);
        const bottom = Math.min(bounds.bottom, visibleBottom);
        if (right <= left || bottom <= top) {
          return;
        }

        pdf.setFillColor(0, 0, 0);
        pdf.rect(
          toPdfX(left),
          toPdfY(top),
          ((right - left) / pageRect.width) * contentWidthIn,
          ((bottom - top) / pageRect.height) * contentHeightIn,
          'F',
        );
      });
    });
};

const addVectorQrCodeToPdf = (
  pdf: jsPDF,
  pageNode: HTMLElement,
  contentWidthIn: number,
  contentHeightIn: number,
  modules: QrModule[],
) => {
  const pageRect = pageNode.getBoundingClientRect();
  const qrNode = pageNode.querySelector<HTMLElement>('.case-simulator-test-sheet-qr');
  if (!qrNode || pageRect.width <= 0 || pageRect.height <= 0) {
    return;
  }

  const bounds = qrNode.getBoundingClientRect();
  const x = PDF_MARGIN_IN + ((bounds.left - pageRect.left) / pageRect.width) * contentWidthIn;
  const y = PDF_MARGIN_IN + ((bounds.top - pageRect.top) / pageRect.height) * contentHeightIn;
  const width = (bounds.width / pageRect.width) * contentWidthIn;
  const height = (bounds.height / pageRect.height) * contentHeightIn;

  pdf.setFillColor(255, 255, 255);
  pdf.rect(x, y, width, height, 'F');
  pdf.setFillColor(0, 0, 0);
  modules.forEach((module) => {
    pdf.rect(
      x + (module.x / TEST_SHEET_QR_VIEWBOX_SIZE) * width,
      y + (module.y / TEST_SHEET_QR_VIEWBOX_SIZE) * height,
      (module.width / TEST_SHEET_QR_VIEWBOX_SIZE) * width,
      (module.height / TEST_SHEET_QR_VIEWBOX_SIZE) * height,
      'F',
    );
  });
};

const CaseSimulatorMainSection: React.FC<CaseSimulatorMainSectionProps> = ({
  createPackLoading,
  games,
  gamesLoading,
  hasStoreId,
  lotteryPayoutLoading,
  lottoSaleLoading,
  locations,
  onCreatePack,
  onGameChange,
  onLocationChange,
  onLotteryPayout,
  onLottoSale,
  onPackReturn,
  processedPacks,
  scannerConfig,
  scratcherPayoutLoading,
  selectedGame,
  selectedLocationId,
  storeId,
  totalPacks,
  downloadActionContainer,
}) => {
  const displayCaseExportRef = React.useRef<HTMLDivElement>(null);
  const [pdfDownloading, setPdfDownloading] = React.useState(false);
  const [testSheetLayout, setTestSheetLayout] = React.useState<TestSheetLayout>('current');
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [renderExportSheet, setRenderExportSheet] = React.useState(false);
  const [exportImageUrls, setExportImageUrls] = React.useState<Map<string, string>>(new Map());
  const [barcodeMode, setBarcodeMode] = React.useState<CaseSimulatorBarcodeMode>('ticket');

  const handleDownloadTestSheet = React.useCallback(async (layout: TestSheetLayout = testSheetLayout) => {
    if (processedPacks.length === 0 || pdfDownloading) {
      return;
    }

    setPdfDownloading(true);
    try {
      const { pageHeightIn, pageWidthIn } = TEST_SHEET_LAYOUTS[layout];
      const contentWidthIn = pageWidthIn - PDF_MARGIN_IN * 2;
      const contentHeightIn = pageHeightIn - PDF_MARGIN_IN * 2;
      const loadedExportImageUrls = await loadCaseSimulatorExportImages(
        processedPacks,
        (imageUrl) => imageService.fetchImageAsBlob(imageUrl),
      );
      const nextExportImageUrls = await makeCanvasSafeImageUrls(loadedExportImageUrls);
      const qrModules = layout === 'tabloid' ? await loadTestSheetQrModules() : [];
      setExportImageUrls(nextExportImageUrls);
      setRenderExportSheet(true);

      const exportNode = await waitForExportSheet(() => displayCaseExportRef.current);

      await document.fonts?.ready;
      await waitForExportImages(exportNode);

      const pdf = new jsPDF({
        format: [pageWidthIn, pageHeightIn],
        orientation: 'portrait',
        unit: 'in',
      });

      if (layout === 'tabloid') {
        const pageNodes = Array.from(
          exportNode.querySelectorAll<HTMLElement>('[data-test-sheet-page="true"]'),
        );
        if (pageNodes.length === 0) {
          throw new Error('Tabloid test sheet pages did not render');
        }

        for (let pageIndex = 0; pageIndex < pageNodes.length; pageIndex += 1) {
          const pageNode = pageNodes[pageIndex];
          const canvas = await html2canvas(pageNode, {
            backgroundColor: '#ffffff',
            ignoreElements: (element) => element.matches([
              '.case-simulator-display-case-barcode svg',
              '.case-simulator-test-sheet-qr',
            ].join(', ')),
            logging: false,
            scale: TABLOID_RASTER_SCALE,
            useCORS: true,
            windowHeight: pageNode.scrollHeight,
            windowWidth: pageNode.scrollWidth,
          });

          if (pageIndex > 0) {
            pdf.addPage([pageWidthIn, pageHeightIn], 'portrait');
          }

          pdf.addImage(
            canvas.toDataURL('image/jpeg', TABLOID_JPEG_QUALITY),
            'JPEG',
            PDF_MARGIN_IN,
            PDF_MARGIN_IN,
            contentWidthIn,
            contentHeightIn,
            undefined,
            'FAST',
          );
          addVectorQrCodeToPdf(pdf, pageNode, contentWidthIn, contentHeightIn, qrModules);
          addVectorBarcodesToPdf(pdf, pageNode, contentWidthIn, contentHeightIn);
        }
      } else {
        const canvas = await html2canvas(exportNode, {
          backgroundColor: '#ffffff',
          logging: false,
          scale: 3,
          useCORS: true,
          windowHeight: exportNode.scrollHeight,
          windowWidth: exportNode.scrollWidth,
        });
        const imageData = canvas.toDataURL('image/png');
        const imageHeightIn = (canvas.height * contentWidthIn) / canvas.width;
        let remainingHeightIn = imageHeightIn;
        let offsetYIn = 0;

        while (remainingHeightIn > 0) {
          if (offsetYIn > 0) {
            pdf.addPage([pageWidthIn, pageHeightIn], 'portrait');
          }

          pdf.addImage(
            imageData,
            'PNG',
            PDF_MARGIN_IN,
            PDF_MARGIN_IN - offsetYIn,
            contentWidthIn,
            imageHeightIn,
            undefined,
            'FAST',
          );

          remainingHeightIn -= contentHeightIn;
          offsetYIn += contentHeightIn;
        }
      }

      pdf.save('lottoreco-test-sheet.pdf');
    } catch (error) {
      console.error('Failed to generate test sheet PDF:', error);
      window.alert('Failed to generate the test sheet PDF. Please try again.');
    } finally {
      setPdfDownloading(false);
      setRenderExportSheet(false);
      setExportImageUrls(new Map());
    }
  }, [pdfDownloading, processedPacks, testSheetLayout]);

  const handleDownloadMenuOpen = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  }, []);

  const handleDownloadMenuClose = React.useCallback(() => {
    setDownloadMenuAnchor(null);
  }, []);

  const handleLayoutSelect = React.useCallback((layout: TestSheetLayout) => {
    setTestSheetLayout(layout);
    setDownloadMenuAnchor(null);
    void handleDownloadTestSheet(layout);
  }, [handleDownloadTestSheet]);

  if (!hasStoreId) {
    return null;
  }

  return (
    <Box>
      {downloadActionContainer && createPortal(
        <>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={processedPacks.length === 0 || pdfDownloading}
            onClick={handleDownloadMenuOpen}
            endIcon={<KeyboardArrowDownRoundedIcon />}
            sx={{
              bgcolor: '#eef8ff !important',
              border: '1px solid #8cc7ef !important',
              boxShadow: 'none',
              color: '#2c6ca0',
              textTransform: 'none',
              fontWeight: 600,
              height: 40,
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: '#eef8ff !important',
                boxShadow: 'none',
              },
            }}
          >
            {pdfDownloading ? 'Preparing test sheet...' : 'Download Test Sheet PDF'}
          </Button>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={handleDownloadMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
          >
            {(Object.keys(TEST_SHEET_LAYOUTS) as TestSheetLayout[]).map((layout) => (
              <MenuItem
                key={layout}
                onClick={() => handleLayoutSelect(layout)}
                selected={layout === testSheetLayout}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                {TEST_SHEET_LAYOUTS[layout].label}
              </MenuItem>
            ))}
          </Menu>
        </>,
        downloadActionContainer,
      )}
      <Box
        className="case-simulator-screen-only"
        sx={{ alignItems: 'flex-end', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mb: 1.5, mt: '12px' }}
      >
        <Stack spacing={1} alignItems="flex-end">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="case-simulator-location-label" shrink>
                Location
              </InputLabel>
              <Select
                labelId="case-simulator-location-label"
                id="case-simulator-location"
                value={selectedLocationId}
                label="Location"
                onChange={onLocationChange}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return 'Select location';
                  }

                  return locations.find((location) => location.id === selected)?.location_name || 'Select location';
                }}
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ActionButtons
              packs={processedPacks}
              lotteryPayoutLoading={lotteryPayoutLoading}
              scratcherPayoutLoading={scratcherPayoutLoading}
              lottoSaleLoading={lottoSaleLoading}
              onLotteryPayout={onLotteryPayout}
              onLottoSale={onLottoSale}
            />
          </Stack>
        </Stack>
      </Box>

      <CreatePackSection
        games={games}
        selectedGame={selectedGame}
        gamesLoading={gamesLoading}
        createPackLoading={createPackLoading}
        onGameChange={onGameChange}
        onCreatePack={onCreatePack}
        totalPacks={totalPacks}
      />

      <Box sx={{ mb: 3 }}>
        <CaseSimulatorBarcodeModeSelector
          barcodeMode={barcodeMode}
          onBarcodeModeChange={setBarcodeMode}
        />
        <CaseSimulatorTicketsGrid
          barcodeMode={barcodeMode}
          processedPacks={processedPacks}
          scannerConfig={scannerConfig}
          onPackReturn={onPackReturn}
          storeId={storeId}
        />
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          left: '-100000px',
          pointerEvents: 'none',
          position: 'fixed',
          top: 0,
          width: '18in',
          zIndex: -1,
        }}
      >
        {renderExportSheet && processedPacks.length > 0 && (
          <CaseSimulatorDisplayCaseGrid
            exportLayout={testSheetLayout}
            rootRef={displayCaseExportRef}
            processedPacks={processedPacks}
            scannerConfig={scannerConfig}
            storeId={storeId}
            imageUrlOverrides={exportImageUrls}
          />
        )}
      </Box>
    </Box>
  );
};

export default React.memo(CaseSimulatorMainSection);
